import React, { createContext, useState, useContext, ReactNode } from "react";
import { parse, formatISO } from "date-fns";
import axiosInstance from "utils/axiosInstance";
import { toast } from "react-toastify";

/** The type of our context’s value. */
type EventsContextType = {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;

  // For storing the "date" text, just like AsistenciaABM does in 'nuevoEvento'
  nuevoEvento: string;
  setNuevoEvento: React.Dispatch<React.SetStateAction<string>>;

  // For storing the "tipo"
  tipo: string;
  setTipo: React.Dispatch<React.SetStateAction<string>>;

  // For storing the "editEvento" ID (if editing an existing event)
  editEvento: string | null;
  setEditEvento: React.Dispatch<React.SetStateAction<string | null>>;

  // For creating or updating an event from anywhere
  crearEvento: () => Promise<void>;
  actualizarEvento: () => Promise<void>;
};

const EventsContext = createContext<EventsContextType>({} as EventsContextType);

export function EventsProvider({ children }: { children: ReactNode }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nuevoEvento, setNuevoEvento] = useState("");
  const [tipo, setTipo] = useState("Fútbol");
  const [editEvento, setEditEvento] = useState<string | null>(null);

  // Open the modal (for creating a new event)
  function openModal() {
    setNuevoEvento("");
    setTipo("Fútbol");
    setEditEvento(null);
    setIsModalOpen(true);
  }

  // Close the modal
  function closeModal() {
    setIsModalOpen(false);
  }
  

  // This logic is similar to 'crearEvento' in AsistenciaABM
  async function crearEvento() {
    if (!nuevoEvento.trim()) {
      toast.error("La fecha del evento es obligatoria.");
      return;
    }
    
    try {
      const parsedDate = parse(nuevoEvento, "dd/MM/yyyy", new Date());
      const formattedDate = formatISO(parsedDate, { representation: "complete" });
      await axiosInstance.post("/eventos", {
        fecha: formattedDate,
        tipo,
      });
      setNuevoEvento("");
      setIsModalOpen(false);
      toast.success("Evento creado con éxito");
    } catch (error) {
      toast.error("Error al crear el evento:", error);
    }
  }

  // Similar to 'actualizarEvento' in AsistenciaABM
  async function actualizarEvento() {
    if (!nuevoEvento.trim()) {
      toast.error("La fecha del evento es obligatoria.");
      return;
    }
    if (!editEvento) return;

    try {
      const parsedDate = parse(nuevoEvento, "dd/MM/yyyy", new Date());
      const formattedDate = formatISO(parsedDate, { representation: "complete" });

      await axiosInstance.put(`/eventos/${editEvento}`, {
        fecha: formattedDate,
        tipo,
      });
      setNuevoEvento("");
      setEditEvento(null);
      setIsModalOpen(false);
      toast.success("Evento actualizado con éxito");
    } catch (error) {
      toast.error("Error al actualizar el evento:", error);
    }
  }

  return (
    <EventsContext.Provider
      value={{
        isModalOpen,
        openModal,
        closeModal,

        nuevoEvento,
        setNuevoEvento,

        tipo,
        setTipo,

        editEvento,
        setEditEvento,

        crearEvento,
        actualizarEvento,
      }}
    >
      {children}
    </EventsContext.Provider>
  );
}

export function useEvents() {
  return useContext(EventsContext);
}
